import { Link } from 'react-router-dom';
import type { Product } from '../data/products';
import { useCart } from '../store/cart';
import { useStockLevel } from '../lib/stock';
import { formatEuro } from '../lib/money';

export default function ProductCard({ product }: { product: Product }) {
  const add = useCart((s) => s.add);
  const stock = useStockLevel(product.id);
  const imageSrc = product.image ? encodeURI(product.image) : null;
  const price = product.price;
  const hasPrice = price !== undefined;
  const soldOut = stock.state === 'out';
  const canPurchase = product.availableForSale !== false && price !== undefined && !soldOut;

  return (
    <article className="card group overflow-hidden hover:border-vapor-400/40 hover:shadow-glow-sm transition-all">
      <Link to={`/producto/${product.slug}`} className="block">
        <div className="aspect-[4/5] bg-gradient-to-br from-ink-700 to-ink-900 relative flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-grid-fade opacity-60" aria-hidden />
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={product.name}
              className={`relative z-10 h-full w-full object-contain p-3 transition-transform group-hover:scale-105 ${
                soldOut ? 'opacity-40 grayscale' : ''
              }`}
              loading="lazy"
            />
          ) : (
            <span className="text-7xl filter drop-shadow-[0_0_20px_rgba(34,211,238,0.4)] transition-transform group-hover:scale-110">
              {product.emoji}
            </span>
          )}

          {soldOut && (
            <span className="absolute top-3 left-3 z-20 rounded-full bg-ink-950/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-bone-200 ring-1 ring-bone-400/40">
              Agotado
            </span>
          )}
          {stock.state === 'low' && (
            <span className="absolute top-3 left-3 z-20 rounded-full bg-amber-400/95 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-950">
              {stock.label}
            </span>
          )}
        </div>

        <div className="p-5">
          {/* Marca y categoría en líneas separadas y de altura fija: así el nombre del
              producto arranca siempre a la misma altura en todas las tarjetas. */}
          <div className="mb-1.5">
            <p className="text-xs text-vapor-400 uppercase tracking-widest truncate">{product.brand}</p>
            <p className="text-[10px] text-bone-400 uppercase tracking-[0.12em] truncate">
              {product.categoryLabel}
            </p>
          </div>
          <h3 className="font-medium text-bone-50 mb-1 leading-snug line-clamp-2 min-h-[2.75rem]">
            {product.name}
          </h3>
          <p className="text-sm text-bone-300 line-clamp-2 min-h-[2.5rem]">{product.shortDescription}</p>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xl font-semibold tracking-tighter">
              {hasPrice ? formatEuro(price) : 'Próximamente'}
            </span>
            {product.priceIsTemporary && (
              <span className="text-[10px] uppercase tracking-widest text-amber-300">Provisional</span>
            )}
          </div>
        </div>
      </Link>

      <div className="px-5 pb-5">
        <button
          disabled={!canPurchase}
          onClick={(e) => {
            e.preventDefault();
            if (!canPurchase) return;
            add(product, 1);
          }}
          className="btn-dark w-full !py-2.5 group-hover:bg-vapor-400 group-hover:text-ink-950 group-hover:border-vapor-400 transition disabled:cursor-not-allowed disabled:opacity-60 disabled:group-hover:bg-transparent disabled:group-hover:text-bone-50 disabled:group-hover:border-ink-500"
        >
          {canPurchase ? 'Añadir al carrito' : soldOut ? 'Agotado' : 'Pendiente de validación'}
        </button>
      </div>
    </article>
  );
}
